import classNames from "classnames";
import { useState } from "react";
import * as React from "react";
import Slider from "react-slick";
import { ImageFlux } from "@components/ImageFlux";
import { OverlayPhoto } from "@components/OverlayPhoto";
import type { UsersShopImageType } from "@graphql/__generated__/types";
import useIsPc from "@hooks/useIsPc";
import { IFImg } from "@libs/utils/IFImg";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import styles from "./index.module.css";
import type { Settings, CustomArrowProps } from "react-slick";

export const PrevArrow: React.FC<CustomArrowProps> = ({ onClick }) => {
  return (
    <div
      className={`${styles.slideArrow} ${styles.slideArrowPrev}`}
      onClick={onClick}
    >
      <img src="/img/slider_arrow_02.svg" alt="" />
    </div>
  );
};

export const NextArrow: React.FC<CustomArrowProps> = ({ onClick }) => {
  return (
    <div
      className={`${styles.slideArrow} ${styles.slideArrowNext}`}
      onClick={onClick}
    >
      <img src="/img/slider_arrow_02.svg" alt="" />
    </div>
  );
};

type Props = {
  shopImageList: UsersShopImageType[];
};

type OverlayPhotoOptions = {
  opened: boolean;
  startIndex: number;
};

// デスクトップ表示で一度に並べる枚数（slidesToShow）
const slidesToShowOnPc = 3;

const VisualSlider: React.FC<Props> = ({ shopImageList }) => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const isPc = useIsPc.useContainer();

  const imageList = shopImageList
    .slice()
    .sort((a, b) => a.masterShopImageId - b.masterShopImageId || a.id - b.id);
  const length = imageList.length;

  // モバイル表示（1枚ずつのフルスライド）で写真が1枚しか無い場合は、ループ・自動再生させない
  const canSlideOnMobile = length > 1;

  // デスクトップ表示（3枚並び・中央寄せ）で写真が3枚未満の場合は、ループ・自動再生させない
  const canSlideOnPc = length >= slidesToShowOnPc;

  // デスクトップ表示で写真が3枚未満の場合は、足りない枠をダミー画像（null）で埋める
  // 1枚: [ダミー, 写真1, ダミー] / 2枚: [写真1, 写真2, ダミー]
  const slideList: Array<UsersShopImageType | null> = (() => {
    if (!isPc || canSlideOnPc || length === 0) {
      return imageList;
    }
    return length === 1 ? [null, imageList[0], null] : [...imageList, null];
  })();
  const isFilledWithDummy = slideList.length > length;

  const SlideSetting: Settings = {
    dots: false,
    slidesToShow: slidesToShowOnPc,
    centerMode: true,
    centerPadding: "0px",
    infinite: canSlideOnPc,
    autoplay: canSlideOnPc,
    autoplaySpeed: 5000,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    afterChange: (currentSlide: number) => {
      setCurrentIndex(currentSlide + 1);
    },
    responsive: [
      {
        breakpoint: 812,
        settings: {
          slidesToShow: 1,
          infinite: canSlideOnMobile,
          autoplay: canSlideOnMobile,
        },
      },
    ],
  };

  const [overlayPhotoOptions, setOverlayPhotoOptions] =
    useState<OverlayPhotoOptions>({ startIndex: 0, opened: false });

  const handleImageClicked = (index: number) => {
    setOverlayPhotoOptions({
      opened: true,
      startIndex: index, // PC版で端の画像が選択されたときはその画像がオーバーレイに表示されるように、currentIndexでななくindexを使用している
    });
    GTMEvent(GTMEvents.shopCarouselPressed);
  };

  return (
    <>
      <div className={styles.slide}>
        <Slider {...SlideSetting}>
          {slideList.map((shopImage, slideIndex) => {
            // ダミー画像はクリックしてもオーバーレイを開かない
            if (!shopImage) {
              return (
                <div className={styles.slideItems} key={`dummy-${slideIndex}`}>
                  <ImageFlux
                    imageType={"shopTop"}
                    url={IFImg("/img/shop_image_dummy.png", true)}
                    alt=""
                  />
                </div>
              );
            }
            return (
              <div
                className={classNames(
                  styles.slideItems,
                  styles.isOverlayPhotoOptions //スライドショーを起動するスライドに付与するClass名（hover処理を追加）
                )}
                key={shopImage.id}
                onClick={() => {
                  // ダミー画像を含めない写真だけの並びでのindexを渡す
                  handleImageClicked(
                    imageList.findIndex((image) => image.id === shopImage.id)
                  );
                }}
              >
                <ImageFlux
                  imageType={"shopTop"}
                  url={shopImage.imageUrl}
                  alt={shopImage.imagePath}
                />
              </div>
            );
          })}
        </Slider>
        {/* ダミー画像で埋めている場合は、中央の写真と枚数表示が一致しないため表示しない */}
        {!isFilledWithDummy && (
          <div className={styles.slideCount}>{`${currentIndex}/${length}`}</div>
        )}
      </div>

      {overlayPhotoOptions.opened && (
        <OverlayPhoto
          defaultActiveIndex={overlayPhotoOptions.startIndex}
          open={overlayPhotoOptions.opened}
          photos={imageList.map((shopImage) => ({
            id: shopImage.id,
            alt: shopImage.imagePath,
            src: shopImage.imageUrl,
          }))}
          onClose={(): void =>
            setOverlayPhotoOptions({ opened: false, startIndex: 0 })
          }
        />
      )}
    </>
  );
};

export default VisualSlider;
